import { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import { ImageBlock } from './extensions/ImageBlock/ImageBlock';
import { VariableNode } from './extensions/VariableNode';
import { Pagination } from './extensions/Pagination';
import { FontSize } from './extensions/FontSize';
import { EditorToolbar } from './toolbar/EditorToolbar';
import { importDocx } from './utils/docx-import';
import { exportDocx } from './utils/docx-export';
import { fileToDataUrl, isAcceptableImage } from './utils/image-upload';
import styles from './ContractEditor.module.css';

export interface ContractEditorRef {
  exportDocx: () => Promise<Blob>;
  getHTML: () => string;
  insertVariable: (name: string) => void;
  insertCustomVariable: (key: string) => void;
  insertImage: (file: File) => Promise<void>;
}

interface ContractEditorProps {
  document: File | null;
  onContentChange?: (html: string) => void;
  onEditorReady?: () => void;
}

const ContractEditor = forwardRef<ContractEditorRef, ContractEditorProps>(
  ({ document: docFile, onContentChange, onEditorReady }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3, 4] },
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Color,
        FontFamily,
        Table.configure({ resizable: true }),
        TableRow,
        TableCell,
        TableHeader,
        ImageBlock.configure({ inline: false, allowBase64: true }),
        Highlight.configure({ multicolor: true }),
        VariableNode,
        Pagination,
        FontSize,
      ],
      editorProps: {
        attributes: {
          class: styles.proseMirror,
        },
        handlePaste: (_view, event) => {
          const items = Array.from(event.clipboardData?.items || []);
          const imageItems = items.filter((it) => it.type.startsWith('image/'));
          if (imageItems.length === 0) return false;

          event.preventDefault();
          (async () => {
            for (const item of imageItems) {
              const file = item.getAsFile();
              if (!file || !isAcceptableImage(file)) continue;
              try {
                const src = await fileToDataUrl(file);
                editor?.chain().focus().setImage({ src }).run();
              } catch (err) {
                console.warn('Falha ao colar imagem:', err);
              }
            }
          })();
          return true;
        },
        handleDrop: (_view, event, _slice, moved) => {
          if (moved) return false;
          const files = Array.from((event as DragEvent).dataTransfer?.files || []);
          const imageFiles = files.filter(isAcceptableImage);
          if (imageFiles.length === 0) return false;

          event.preventDefault();
          (async () => {
            for (const file of imageFiles) {
              try {
                const src = await fileToDataUrl(file);
                editor?.chain().focus().setImage({ src }).run();
              } catch (err) {
                console.warn('Falha ao soltar imagem:', err);
              }
            }
          })();
          return true;
        },
      },
      onUpdate: ({ editor: ed }) => {
        onContentChange?.(ed.getHTML());
      },
    });

    // Se não tem documento (modo em branco), focar o editor para permitir digitação
    useEffect(() => {
      if (!editor || docFile) return;
      editor.commands.focus();
      onEditorReady?.();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, docFile]);

    // Carregar DOCX quando o arquivo muda
    useEffect(() => {
      if (!docFile || !editor) return;

      let cancelled = false;

      const loadDocument = async () => {
        setIsLoading(true);
        setError(null);

        try {
          const html = await importDocx(docFile);

          if (!cancelled) {
            editor.commands.setContent(html);
            // setContent não dispara onUpdate. Sincroniza o conteúdo inicial com o pai
            // para que extrações dependentes do HTML (ex: variáveis personalizadas no
            // PlaceholdersPanel) reflitam o que foi importado.
            onContentChange?.(editor.getHTML());
            onEditorReady?.();
          }
        } catch (err) {
          console.error('Erro ao importar DOCX:', err);
          if (!cancelled) {
            setError(
              'Erro ao carregar o documento. Verifique se é um arquivo DOCX válido.'
            );
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      };

      loadDocument();

      return () => {
        cancelled = true;
      };
    }, [docFile, editor]);

    // Expor API via ref
    useImperativeHandle(
      ref,
      () => ({
        exportDocx: async (): Promise<Blob> => {
          if (!editor) throw new Error('Editor não está pronto');
          const html = editor.getHTML();
          return exportDocx(html);
        },
        getHTML: (): string => {
          return editor?.getHTML() || '';
        },
        insertVariable: (name: string) => {
          if (!editor) return;
          editor.commands.insertVariable(name);
        },
        insertCustomVariable: (key: string) => {
          if (!editor) return;
          // VariableNode detecta isCustomVariable(key) automaticamente e marca data-custom.
          editor.commands.insertVariable(key);
        },
        insertImage: async (file: File) => {
          if (!editor) return;
          if (!isAcceptableImage(file)) {
            throw new Error('Arquivo de imagem inválido (apenas imagens até 5 MB)');
          }
          const src = await fileToDataUrl(file);
          editor.chain().focus().setImage({ src }).run();
        },
      }),
      [editor]
    );

    return (
      <div className={styles.contractEditor}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner}></div>
            <p>Carregando documento...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorOverlay}>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>
              Tentar novamente
            </button>
          </div>
        )}

        <EditorToolbar editor={editor} />

        <div className={styles.editorContent} data-tour="editor-area">
          <div className={styles.page} data-page="true">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    );
  }
);

ContractEditor.displayName = 'ContractEditor';

export default ContractEditor;
